import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { resolveImageUrl } from '../lib/imageUrl';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Heart, ShoppingCart, Search } from 'lucide-react';
import { toast } from 'sonner';

const ShopPage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addToCart } = useCart();
  const [products, setProducts] = useState([]);
  const [visibleCount, setVisibleCount] = useState(40);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    loadProducts();
    loadFavorites();
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadCategories = async () => {
    try {
      const res = await api.get('/categories');
      setCategories(res.data || []);
    } catch (e) {
      console.error('Failed to load categories', e);
    }
  };

  const applyFilters = (newSearch, newCategory) => {
    const params = {};
    if (newCategory) params.category = newCategory;
    if (newSearch) params.search = newSearch;
    setSearchParams(params);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    applyFilters(searchTerm, selectedCategory);
  };

  const loadProducts = async () => {
    try {
      const category = searchParams.get('category');
      const search = searchParams.get('search');
      const params = {};
      if (category) params.category_id = category;
      if (search) params.search = search;
      
      const response = await api.get('/products', { params });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to load products', error);
      toast.error(t('errorLoadingProducts'));
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await api.get('/favorites');
      setFavorites(response.data.map((p) => p.id));
    } catch (error) {
      console.error('Failed to load favorites', error);
    }
  };

  const toggleFavorite = async (productId) => {
    try {
      if (favorites.includes(productId)) {
        await api.delete(`/favorites/${productId}`);
        setFavorites(favorites.filter((id) => id !== productId));
        toast.success(t('removedFromFavorites'));
      } else {
        await api.post(`/favorites/${productId}`);
        setFavorites([...favorites, productId]);
        toast.success(t('addedToFavorites'));
      }
    } catch (error) {
      toast.error(t('errorUpdatingFavorites'));
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success(t('addedToCart'));
  };

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <h1 className="text-3xl md:text-4xl font-heading font-bold mb-6 uppercase" data-testid="shop-title">
        {t('products')}
      </h1>

      <div className="flex flex-col md:flex-row gap-3 mb-8">
        <form onSubmit={handleSearchSubmit} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder={t('searchProducts')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ps-10 pe-10"
            data-testid="shop-search-input"
          />
        </form>
        <select
          value={selectedCategory}
          onChange={(e) => applyFilters(searchTerm, e.target.value)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm md:w-64"
          data-testid="shop-category-filter"
        >
          <option value="">{t('allCategories')}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12">{t('loading')}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">{t('noProducts')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.slice(0, visibleCount).map((product) => (
            <div
              key={product.id}
              className="product-card bg-white border border-border rounded-md overflow-hidden"
              data-testid={`product-card-${product.id}`}
            >
              <div
                className="aspect-square bg-muted cursor-pointer"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <img
                  src={resolveImageUrl(product.image_url)}
                  alt={product.name}
                  className="w-full h-full object-contain p-4 bg-white"
                  loading="lazy"
                />
              </div>

              <div className="p-4">
                <h3
                  className="font-heading font-semibold text-lg mb-2 cursor-pointer hover:text-primary"
                  onClick={() => navigate(`/product/${product.id}`)}
                  data-testid={`product-name-${product.id}`}
                >
                  {product.name}
                </h3>
                <p className="text-2xl font-bold mb-4" data-testid={`product-price-${product.id}`}>
                  €{product.price.toFixed(2)}
                </p>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleAddToCart(product)}
                    className="flex-1"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="h-4 w-4 me-2" />
                    {t('addToCart')}
                  </Button>

                  <Button
                    variant={favorites.includes(product.id) ? 'default' : 'outline'}
                    size="icon"
                    onClick={() => toggleFavorite(product.id)}
                    data-testid={`favorite-button-${product.id}`}
                  >
                    <Heart
                      className={`h-4 w-4 ${favorites.includes(product.id) ? 'fill-current' : ''}`}
                    />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          </div>
          {visibleCount < products.length && (
            <div className="flex flex-col items-center gap-3 mt-8" data-testid="shop-load-more-wrap">
              <p className="text-sm text-muted-foreground">
                {t('shownOfProducts', { shown: visibleCount, total: products.length })}
              </p>
              <Button
                variant="outline"
                onClick={() => setVisibleCount((v) => v + 40)}
                data-testid="shop-load-more-btn"
              >
                {t('showMoreProducts')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopPage;
